import Seo from "@/components/feature/Seo";
import Layout from "@/components/feature/Layout";
import ComicCard, { statusStyle } from "@/components/feature/ComicCard";
import SectionHeader from "@/components/base/SectionHeader";
import { comics, getComicById } from "@/mocks/comics";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialChapter = Number(searchParams.get("chapter")) || 0;
  const [selectedChapter, setSelectedChapter] = useState(initialChapter);

  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-28 text-center md:px-6">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            未找到该漫画
          </h1>
          <p className="mt-2 text-sm text-foreground-500">作品可能已被移动或下架。</p>
          <button
            onClick={() => navigate("/comics")}
            className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
          >
            返回漫画库
          </button>
        </div>
      </Layout>
    );
  }

  const related = comics
    .filter(
      (c) =>
        c.id !== comic.id &&
        c.categories.some((cat) => comic.categories.includes(cat))
    )
    .slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${comic.title} 章节列表`,
    itemListElement: comic.chapters.map((ch, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: ch.title,
      url: `https://manhwanalysis.github.io/comic/${comic.id}?chapter=${ch.num}`,
    })),
  };

  return (
    <Layout>
      <Seo
        title={`${comic.title}漫画 | ${comic.author} · ${comic.latestChapter} · 笨狗漫画网`}
        description={`${comic.title}漫画最新章节${comic.latestChapter}已更新，${comic.description.slice(0, 60)}…`}
        keywords={`${comic.title},${comic.author},${comic.tags.join(",")},笨狗漫画网`}
        canonical={`https://manhwanalysis.github.io/comic/${comic.id}`}
        type="book"
        jsonLd={jsonLd}
      />

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 cursor-pointer">
              首页
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={() => navigate("/comics")} className="hover:text-primary-600 cursor-pointer">
              漫画分类
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">{comic.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row">
            <div className="w-full shrink-0 md:w-56">
              <img
                src={comic.cover}
                alt={`${comic.title}漫画封面`}
                title={`${comic.title} · ${comic.totalChapters}`}
                className="w-full rounded-xl border border-background-200 object-cover md:w-56"
              />
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[comic.status]}`}>
                  {comic.status}
                </span>
                {comic.isHot && (
                  <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                    <i className="ri-fire-fill"></i> 热门
                  </span>
                )}
              </div>
              <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                {comic.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">
                <i className="ri-pen-nib-line mr-1 text-primary-600"></i>
                作者：{comic.author}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-lg font-bold text-accent-600">
                    <i className="ri-star-fill"></i>
                    {comic.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500">评分</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-900">{comic.views}</p>
                  <p className="text-xs text-foreground-500">人气</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-900">{comic.favorites}</p>
                  <p className="text-xs text-foreground-500">收藏</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-900">{comic.totalChapters}</p>
                  <p className="text-xs text-foreground-500">章节</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                  >
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate(`/comic/${comic.id}?chapter=${comic.chapters[0].num}`)}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-1"></i>开始阅读
                </button>
                <button
                  onClick={() => navigate(`/comic/${comic.id}?chapter=${comic.chapters[0].num}`)}
                  className="whitespace-nowrap rounded-full border border-primary-300 bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                >
                  继续阅读 · {comic.latestChapter}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="font-heading text-lg font-extrabold text-foreground-950">
            <i className="ri-file-list-3-line mr-2 text-primary-600"></i>作品简介
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.description}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-10 md:px-6">
        <SectionHeader
          title="章节列表"
          subtitle={`共 ${comic.totalChapters}，最新章节 ${comic.latestChapter}`}
          accent
        />
        <div className="rounded-xl border border-background-200 bg-background-50">
          <ul className="divide-y divide-background-100">
            {comic.chapters.map((ch, idx) => (
              <li
                key={ch.num}
                onClick={() => setSelectedChapter(ch.num)}
                className={`flex cursor-pointer items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-background-100 ${
                  selectedChapter === ch.num && selectedChapter !== 0
                    ? "bg-primary-50"
                    : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background-100 text-xs font-semibold text-foreground-600">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium text-foreground-900">
                    {ch.num === comic.chapters[0].num && selectedChapter === 0 ? "最新 " : ""}
                    {ch.title}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <span className="hidden sm:inline">
                    <i className="ri-eye-line mr-1"></i>
                    {(ch.readCount / 10000).toFixed(1)}万
                  </span>
                  <span>{ch.updateTime}</span>
                  <i className="ri-arrow-right-s-line text-base"></i>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {selectedChapter !== 0 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-primary-200 bg-primary-50 px-5 py-4">
            <p className="text-sm font-semibold text-primary-800">
              当前选择：{comic.title} · 第{selectedChapter}话
            </p>
            <button className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
              开始阅读
            </button>
          </div>
        )}
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <SectionHeader title="相关推荐" subtitle="同类型作品，可能你也喜欢" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}